import React, { useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { Box, Button, Typography } from "@material-ui/core";

const useStyles = makeStyles({
  fieldButtons: {
    display: "flex",
    justifyContent: "center",
    padding: "10px 0",
    gap: "10px",
  },
  cancel: {
    width: "80px",
    height: "30px",
    "& p": {
      fontSize: "0.8rem",
    },
  },
  // There was a green `submit` style here that no button referenced. It has been dropped
  // rather than applied: every call site is a delete confirmation, and styling a
  // destructive action as the affirmative green one would be misleading.
});

/**
 * Confirm/cancel pair for a confirmation dialog.
 *
 * Runs `handleFunction` and closes ONLY if it resolves, so a failed action leaves the
 * dialog open for another try; the caller is responsible for reporting why.
 *
 * It previously documented `submitType` / `closeOnSuccess` props for switching between
 * "form submit" and "confirm dialog" behaviour, but the handler that read them was never
 * wired to a button — the props did nothing, and every call site is a confirm dialog. They
 * have been removed rather than left as a promise the component doesn't keep.
 */
const ButtonsGroup = ({
  handleClose,
  handleFunction,
  submitTitle,
  closeTitle,
  isDisabled,
}) => {
  const classes = useStyles();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (typeof handleFunction !== "function") {
      handleClose?.();
      return;
    }

    try {
      setIsSubmitting(true);
      await handleFunction();
      handleClose?.();
      // No state reset on success: handleClose unmounts this, and setting state
      // afterwards warns on React 17.
    } catch (error) {
      setIsSubmitting(false);
    }
  };

  return (
    <Box className={classes.fieldButtons}>
      <Button
        // Never "submit": this can be rendered inside a form and must not trigger one.
        type="button"
        variant="contained"
        disabled={isDisabled || isSubmitting}
        onClick={handleConfirm}
      >
        <Typography>{submitTitle}</Typography>
      </Button>

      <Button
        className={classes.cancel}
        variant="outlined"
        disabled={isSubmitting}
        onClick={handleClose}
      >
        <Typography>{closeTitle}</Typography>
      </Button>
    </Box>
  );
};

ButtonsGroup.propTypes = {
  handleClose: PropTypes.func.isRequired,
  handleFunction: PropTypes.func,
  submitTitle: PropTypes.string.isRequired,
  closeTitle: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool,
};

ButtonsGroup.defaultProps = {
  handleFunction: undefined,
  isDisabled: false,
};

export default ButtonsGroup;
